import { useState, useEffect } from 'react'
import {
  Modal,
  Steps,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Alert,
  Card,
  Descriptions,
  Switch,
  InputNumber,
  message,
} from 'antd'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { featureDefinitionsApi } from '@/api/feature-definitions'
import type {
  CreateFeatureDefinitionInput,
  UpdateFeatureDefinitionInput,
  FeatureDefinition,
  StorageLocation,
  AvailabilityRule,
  DataType,
} from '@/types/feature-definition'

const { TextArea } = Input
const { Text } = Typography

interface FeatureDefinitionWizardProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  editMode?: boolean
  initialData?: FeatureDefinition
}

interface FormData {
  // Step 1: Basic Info
  key: string
  name: string
  description?: string

  // Step 2: Technical Config
  categoryId: string
  storageLocation: StorageLocation
  storagePath?: string
  dataType: DataType

  // Step 3: Default Value & Validation
  defaultValue?: any
  validationSchema?: string // JSON string

  // Step 4: Availability Rules
  availabilityRule: AvailabilityRule
  availabilityConditions?: string // JSON string

  // Step 5: UI Configuration
  uiConfig?: string // JSON string
}

export const FeatureDefinitionWizard: React.FC<FeatureDefinitionWizardProps> = ({
  open,
  onClose,
  onSuccess,
  editMode = false,
  initialData,
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [form] = Form.useForm<FormData>()
  const queryClient = useQueryClient()

  // JSON validation error states
  const [validationJsonError, setValidationJsonError] = useState<string>('')
  const [conditionsJsonError, setConditionsJsonError] = useState<string>('')
  const [jsonError, setJsonError] = useState<string>('')

  // Query for categories
  const { data: categoriesData } = useQuery({
    queryKey: ['feature-categories'],
    queryFn: () => featureDefinitionsApi.listCategories(),
    enabled: open,
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateFeatureDefinitionInput) => featureDefinitionsApi.create(data),
    onSuccess: () => {
      message.success('Feature definition created successfully')
      queryClient.invalidateQueries({ queryKey: ['feature-definitions'] })
      onSuccess?.()
      handleClose()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create feature definition')
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFeatureDefinitionInput }) =>
      featureDefinitionsApi.update(id, data),
    onSuccess: () => {
      message.success('Feature definition updated successfully')
      queryClient.invalidateQueries({ queryKey: ['feature-definitions'] })
      onSuccess?.()
      handleClose()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update feature definition')
    },
  })

  // Initialize form with edit data
  useEffect(() => {
    if (editMode && initialData && open) {
      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description || undefined,
        categoryId: initialData.category_id || undefined,
        storageLocation: initialData.storage_location,
        storagePath: initialData.storage_path || undefined,
        dataType: initialData.data_type,
        defaultValue: initialData.default_value,
        validationSchema: initialData.validation_schema
          ? JSON.stringify(initialData.validation_schema, null, 2)
          : undefined,
        availabilityRule: initialData.availability_rule,
        availabilityConditions: initialData.availability_conditions
          ? JSON.stringify(initialData.availability_conditions, null, 2)
          : undefined,
        uiConfig: initialData.ui_config
          ? JSON.stringify(initialData.ui_config, null, 2)
          : undefined,
      })
    }
  }, [editMode, initialData, open, form])

  const handleClose = () => {
    form.resetFields()
    setCurrentStep(0)
    onClose()
  }

  const handleNext = async () => {
    try {
      // Validate current step fields
      const stepFields = getStepFields(currentStep)
      await form.validateFields(stepFields)
      setCurrentStep(currentStep + 1)
    } catch (error) {
      // Validation failed, form will show errors
      console.log('Validation failed:', error)
    }
  }

  const handlePrev = () => {
    setCurrentStep(currentStep - 1)
  }

  const handleSubmit = async () => {
    try {
      // Validate all required fields explicitly
      const requiredFields = [
        'key',
        'name',
        'categoryId',
        'storageLocation',
        'dataType',
        'availabilityRule',
      ]

      // Check if required fields have values
      const allValues = form.getFieldsValue(true)
      const missingFields = requiredFields.filter((field) => !allValues[field])

      if (missingFields.length > 0) {
        message.error(`Missing required fields: ${missingFields.join(', ')}`)
        return
      }

      // Get all form values (including from previous steps)
      const values = allValues

      // Helper to safely parse JSON
      const safeJsonParse = (str: string | undefined) => {
        if (!str || str.trim() === '') return undefined
        try {
          return JSON.parse(str)
        } catch (e) {
          throw new Error(`Invalid JSON: ${e instanceof Error ? e.message : 'Parse error'}`)
        }
      }

      // Parse JSON fields
      const parsedValues: any = {
        ...values,
        validationSchema: safeJsonParse(values.validationSchema),
        availabilityConditions: safeJsonParse(values.availabilityConditions),
        uiConfig: safeJsonParse(values.uiConfig),
      }

      // Handle defaultValue based on dataType
      if (parsedValues.dataType === 'json' && typeof parsedValues.defaultValue === 'string') {
        parsedValues.defaultValue = safeJsonParse(parsedValues.defaultValue)
      }

      if (editMode && initialData) {
        // Update mode
        const updateData: UpdateFeatureDefinitionInput = {
          name: parsedValues.name,
          description: parsedValues.description,
          categoryId: parsedValues.categoryId,
          storagePath: parsedValues.storagePath,
          dataType: parsedValues.dataType,
          defaultValue: parsedValues.defaultValue,
          validationSchema: parsedValues.validationSchema,
          availabilityRule: parsedValues.availabilityRule,
          availabilityConditions: parsedValues.availabilityConditions,
          uiConfig: parsedValues.uiConfig,
        }
        updateMutation.mutate({ id: initialData.id, data: updateData })
      } else {
        // Create mode
        const createData: CreateFeatureDefinitionInput = {
          key: parsedValues.key,
          name: parsedValues.name,
          description: parsedValues.description,
          categoryId: parsedValues.categoryId,
          storageLocation: parsedValues.storageLocation,
          storagePath: parsedValues.storagePath,
          dataType: parsedValues.dataType,
          defaultValue: parsedValues.defaultValue,
          validationSchema: parsedValues.validationSchema,
          availabilityRule: parsedValues.availabilityRule || 'all',
          availabilityConditions: parsedValues.availabilityConditions,
          uiConfig: parsedValues.uiConfig,
        }
        createMutation.mutate(createData)
      }
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please check all required fields')
      } else if (error.message) {
        message.error(error.message)
      }
    }
  }

  const getStepFields = (step: number): string[] => {
    switch (step) {
      case 0:
        return ['key', 'name', 'description']
      case 1:
        return ['categoryId', 'storageLocation', 'storagePath', 'dataType']
      case 2:
        return ['defaultValue', 'validationSchema']
      case 3:
        return ['availabilityRule', 'availabilityConditions']
      case 4:
        return ['uiConfig']
      default:
        return []
    }
  }

  const steps = [
    {
      title: 'Basic',
      description: 'Key & name',
    },
    {
      title: 'Technical',
      description: 'Storage & type',
    },
    {
      title: 'Defaults',
      description: 'Values',
    },
    {
      title: 'Rules',
      description: 'Availability',
    },
    {
      title: 'UI',
      description: 'Display',
    },
    {
      title: 'Review',
      description: 'Confirm',
    },
  ]

  const renderStepContent = () => {
    const dataType = form.getFieldValue('dataType')

    switch (currentStep) {
      case 0:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="Basic Information"
              description="Enter the basic details of the feature definition. The key must be unique and cannot be changed later."
              type="info"
              showIcon
            />
            <Form.Item
              label="Key"
              name="key"
              rules={[
                { required: true, message: 'Please enter a feature key' },
                {
                  pattern: /^[a-zA-Z][a-zA-Z0-9_-]*$/,
                  message:
                    'Key must be alphanumeric with hyphens, underscores, or camelCase (e.g., record-intake, record_intake, recordIntake)',
                },
              ]}
              extra="Unique identifier (e.g., record-intake, enable_auto_routing, aiXcel)"
            >
              <Input placeholder="record-intake" disabled={editMode} />
            </Form.Item>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter a display name' }]}
              extra="Human-readable display name"
            >
              <Input placeholder="Enable Auto Routing" />
            </Form.Item>
            <Form.Item label="Description" name="description">
              <TextArea
                rows={4}
                placeholder="Detailed description of what this feature does and when to use it"
              />
            </Form.Item>
          </Space>
        )

      case 1:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="Technical Configuration"
              description="Configure how and where this feature is stored in the system."
              type="info"
              showIcon
            />
            <Form.Item
              label="Category"
              name="categoryId"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select
                placeholder="Select category"
                options={categoriesData?.data?.map((cat) => ({
                  label: cat.display_name,
                  value: cat.id,
                }))}
              />
            </Form.Item>
            <Form.Item
              label="Storage Location"
              name="storageLocation"
              rules={[{ required: true, message: 'Please select storage location' }]}
              extra="Where the feature value will be stored"
            >
              <Select
                placeholder="Select storage location"
                options={[
                  { label: 'Legacy Extra Properties', value: 'legacy_extra' },
                  { label: 'Legacy Features JSON', value: 'legacy_features' },
                  { label: 'Direct Table Attributes', value: 'attributes' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Storage Path"
              name="storagePath"
              extra="Path within the storage location (optional)"
            >
              <Input placeholder="e.g., extra.customProperty" />
            </Form.Item>
            <Form.Item
              label="Data Type"
              name="dataType"
              rules={[{ required: true, message: 'Please select data type' }]}
            >
              <Select
                placeholder="Select data type"
                options={[
                  { label: 'Boolean (true/false)', value: 'boolean' },
                  { label: 'String (text)', value: 'string' },
                  { label: 'Number (integer/decimal)', value: 'number' },
                  { label: 'JSON (complex object)', value: 'json' },
                ]}
              />
            </Form.Item>
          </Space>
        )

      case 2:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="Default Value & Validation"
              description="Set the default value and optional validation schema."
              type="info"
              showIcon
            />
            <Form.Item
              label="Default Value"
              name="defaultValue"
              extra={`Enter the default value for ${dataType || 'this'} type`}
            >
              {dataType === 'boolean' ? (
                <Switch />
              ) : dataType === 'number' ? (
                <InputNumber style={{ width: '100%' }} />
              ) : dataType === 'json' ? (
                <TextArea rows={6} placeholder='{"key": "value"}' />
              ) : (
                <Input placeholder="Default value" />
              )}
            </Form.Item>

            <Card title="JSON Schema Validation (Optional)" size="small" style={{ marginTop: 16 }}>
              <Text>
                Use{' '}
                <a href="https://json-schema.org/" target="_blank" rel="noopener noreferrer">
                  JSON Schema
                </a>{' '}
                to validate feature values. Common properties:
              </Text>
              <ul style={{ marginTop: 8, marginBottom: 0, fontSize: '13px' }}>
                <li>
                  <Text code>type</Text>: Data type ("string", "number", "boolean", "object")
                </li>
                <li>
                  <Text code>minLength</Text> / <Text code>maxLength</Text>: String length
                  constraints
                </li>
                <li>
                  <Text code>minimum</Text> / <Text code>maximum</Text>: Number range constraints
                </li>
                <li>
                  <Text code>pattern</Text>: Regex pattern for string validation
                </li>
                <li>
                  <Text code>enum</Text>: Allowed values array
                </li>
              </ul>
            </Card>

            <Form.Item
              label="Validation Schema"
              name="validationSchema"
              validateStatus={validationJsonError ? 'error' : ''}
              help={validationJsonError || 'JSON Schema for runtime validation (optional)'}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.trim() === '') {
                      setValidationJsonError('')
                      return Promise.resolve()
                    }
                    try {
                      JSON.parse(value)
                      setValidationJsonError('')
                      return Promise.resolve()
                    } catch (e) {
                      const error = `Invalid JSON: ${e instanceof Error ? e.message : 'Parse error'}`
                      setValidationJsonError(error)
                      return Promise.reject(error)
                    }
                  },
                },
              ]}
            >
              <TextArea
                rows={8}
                placeholder={
                  '{\n  "type": "string",\n  "minLength": 1,\n  "maxLength": 100,\n  "pattern": "^[a-z0-9._-]+@[a-z0-9.-]+\\\\.[a-z]{2,}$"\n}'
                }
                onChange={() => form.validateFields(['validationSchema'])}
              />
            </Form.Item>
          </Space>
        )

      case 3: {
        const showCustomConditions = form.getFieldValue('availabilityRule') === 'custom'

        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="Availability Rules"
              description="Define which hospital types can use this feature."
              type="info"
              showIcon
            />
            <Form.Item
              label="Availability Rule"
              name="availabilityRule"
              initialValue="all"
              rules={[{ required: true }]}
              extra="Determines which hospitals can use this feature based on their type (participant/receiver)"
            >
              <Select
                options={[
                  {
                    label: 'All Hospitals',
                    value: 'all',
                  },
                  {
                    label: 'Participant Hospitals Only',
                    value: 'participant_only',
                  },
                  {
                    label: 'Receiver Hospitals Only',
                    value: 'receiver_only',
                  },
                  {
                    label: 'Custom Rules (JSON)',
                    value: 'custom',
                  },
                ]}
                onChange={() => {
                  // Clear conditions when switching away from custom
                  if (form.getFieldValue('availabilityRule') !== 'custom') {
                    form.setFieldValue('availabilityConditions', undefined)
                  }
                }}
              />
            </Form.Item>

            {showCustomConditions && (
              <>
                <Alert
                  message="Custom Availability Rules"
                  description="Define custom logic for determining feature availability. This is evaluated at runtime when checking if a hospital can use the feature."
                  type="warning"
                  showIcon
                />
                <Form.Item
                  label="Custom Conditions"
                  name="availabilityConditions"
                  validateStatus={conditionsJsonError ? 'error' : ''}
                  help={
                    conditionsJsonError ||
                    'JSON object defining custom availability logic (implementation-specific)'
                  }
                  rules={[
                    {
                      required: showCustomConditions,
                      message: 'Custom conditions are required when using custom rules',
                    },
                    {
                      validator: async (_, value) => {
                        if (!value || value.trim() === '') {
                          if (!showCustomConditions) {
                            setConditionsJsonError('')
                            return Promise.resolve()
                          }
                          return Promise.reject('Custom conditions required')
                        }
                        try {
                          JSON.parse(value)
                          setConditionsJsonError('')
                          return Promise.resolve()
                        } catch (e) {
                          const error = `Invalid JSON: ${e instanceof Error ? e.message : 'Parse error'}`
                          setConditionsJsonError(error)
                          return Promise.reject(error)
                        }
                      },
                    },
                  ]}
                >
                  <TextArea
                    rows={8}
                    placeholder={
                      '{\n  "requiresBothTypes": true,\n  "minBedCapacity": 10,\n  "hasEmergencyDept": true\n}'
                    }
                    onChange={() => form.validateFields(['availabilityConditions'])}
                  />
                </Form.Item>
              </>
            )}
          </Space>
        )
      }

      case 4:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="UI Configuration (Optional)"
              description="Define how this feature should be rendered in the hospital feature assignment UI."
              type="info"
              showIcon
            />

            <Card title="UI Config Schema" size="small" style={{ marginBottom: 16 }}>
              <Text strong>Available Properties:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>
                  <Text code>inputType</Text>: <Text type="secondary">string</Text> - Input control
                  type
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px', paddingLeft: 16 }}>
                    Options: "switch" | "text" | "textarea" | "number" | "select" | "json"
                  </Text>
                </li>
                <li>
                  <Text code>helpText</Text>: <Text type="secondary">string</Text> - Help text
                  displayed below the input
                </li>
                <li>
                  <Text code>placeholder</Text>: <Text type="secondary">string</Text> - Input
                  placeholder text
                </li>
                <li>
                  <Text code>section</Text>: <Text type="secondary">string</Text> - Section/group
                  name for organizing features
                </li>
                <li>
                  <Text code>options</Text>: <Text type="secondary">array</Text> - For select inputs
                  (e.g., [{`{label: "Option 1", value: "opt1"}`}])
                </li>
                <li>
                  <Text code>min</Text> / <Text code>max</Text>:{' '}
                  <Text type="secondary">number</Text> - Min/max values for number inputs
                </li>
                <li>
                  <Text code>rows</Text>: <Text type="secondary">number</Text> - Rows for textarea
                  inputs
                </li>
              </ul>
            </Card>

            <Form.Item
              label="UI Config JSON"
              name="uiConfig"
              validateStatus={jsonError ? 'error' : ''}
              help={jsonError || 'Leave empty to use default rendering based on data type'}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.trim() === '') {
                      setJsonError('')
                      return Promise.resolve()
                    }
                    try {
                      JSON.parse(value)
                      setJsonError('')
                      return Promise.resolve()
                    } catch (e) {
                      const error = `Invalid JSON: ${e instanceof Error ? e.message : 'Parse error'}`
                      setJsonError(error)
                      return Promise.reject(error)
                    }
                  },
                },
              ]}
            >
              <TextArea
                rows={12}
                placeholder={`Example:\n{\n  "inputType": "switch",\n  "helpText": "Enable advanced routing with ML predictions",\n  "section": "Advanced Features"\n}`}
                onChange={(_e) => {
                  // Trigger validation on change
                  form.validateFields(['uiConfig'])
                }}
              />
            </Form.Item>

            <Alert
              message="Example Configurations"
              description={
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Boolean (Switch):</Text>
                    <pre style={{ fontSize: '11px', margin: '4px 0' }}>
                      {`{"inputType": "switch", "helpText": "Enable this feature"}`}
                    </pre>
                  </div>
                  <div>
                    <Text strong>String (Text Input):</Text>
                    <pre style={{ fontSize: '11px', margin: '4px 0' }}>
                      {`{"inputType": "text", "placeholder": "Enter email", "helpText": "Notification email address"}`}
                    </pre>
                  </div>
                  <div>
                    <Text strong>Number with Range:</Text>
                    <pre style={{ fontSize: '11px', margin: '4px 0' }}>
                      {`{"inputType": "number", "min": 1, "max": 100, "helpText": "Maximum concurrent transfers"}`}
                    </pre>
                  </div>
                  <div>
                    <Text strong>Select Dropdown:</Text>
                    <pre style={{ fontSize: '11px', margin: '4px 0' }}>
                      {`{"inputType": "select", "options": [{"label": "Low", "value": "low"}, {"label": "High", "value": "high"}]}`}
                    </pre>
                  </div>
                </Space>
              }
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Space>
        )

      case 5: {
        // Review step
        const values = form.getFieldsValue(true)
        const category = categoriesData?.data?.find((c) => c.id === values.categoryId)

        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="Review Feature Definition"
              description="Please review the details before submitting."
              type="success"
              showIcon
            />
            <Card title="Basic Information">
              <Descriptions column={1}>
                <Descriptions.Item label="Key">{values.key}</Descriptions.Item>
                <Descriptions.Item label="Name">{values.name}</Descriptions.Item>
                <Descriptions.Item label="Description">
                  {values.description || <Text type="secondary">—</Text>}
                </Descriptions.Item>
              </Descriptions>
            </Card>
            <Card title="Technical Configuration">
              <Descriptions column={1}>
                <Descriptions.Item label="Category">{category?.display_name}</Descriptions.Item>
                <Descriptions.Item label="Storage Location">
                  {values.storageLocation}
                </Descriptions.Item>
                <Descriptions.Item label="Storage Path">
                  {values.storagePath || <Text type="secondary">—</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Data Type">{values.dataType}</Descriptions.Item>
              </Descriptions>
            </Card>
            <Card title="Default Value & Availability">
              <Descriptions column={1}>
                <Descriptions.Item label="Default Value">
                  {values.defaultValue !== undefined ? (
                    String(values.defaultValue)
                  ) : (
                    <Text type="secondary">—</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Availability Rule">
                  {values.availabilityRule}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Space>
        )
      }

      default:
        return null
    }
  }

  return (
    <Modal
      title={editMode ? 'Edit Feature Definition' : 'Create Feature Definition'}
      open={open}
      onCancel={handleClose}
      width={900}
      footer={null}
      destroyOnHidden
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Steps current={currentStep} items={steps} />

        <Form form={form} layout="vertical" preserve>
          {renderStepContent()}
        </Form>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Space>
            {currentStep > 0 && <Button onClick={handlePrev}>Previous</Button>}
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={handleNext}>
                Next
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editMode ? 'Update' : 'Create'}
              </Button>
            )}
          </Space>
        </div>
      </Space>
    </Modal>
  )
}
